import type { EditorHandle } from "@codeshare/editor";
import { useEffect, useState } from "react";

export interface EditorStatus {
  line: number;
  column: number;
  /** Characters selected, across all selections. */
  selected: number;
  tabSize: number;
  insertSpaces: boolean;
  eol: "LF" | "CRLF";
}

const INITIAL: EditorStatus = {
  line: 1,
  column: 1,
  selected: 0,
  tabSize: 2,
  insertSpaces: true,
  eol: "LF",
};

/** What VS Code's status bar shows about the editor: cursor, selection, indentation, line ends. */
export function useEditorStatus(handle: EditorHandle | undefined): EditorStatus {
  const [status, setStatus] = useState(INITIAL);
  useEffect(() => {
    if (!handle) return;
    const { editor } = handle;
    const read = () => {
      const model = editor.getModel();
      const position = editor.getPosition();
      if (!model || !position) return;
      const options = model.getOptions();
      const selected = (editor.getSelections() ?? []).reduce(
        (sum, selection) => sum + model.getValueLengthInRange(selection),
        0,
      );
      setStatus({
        line: position.lineNumber,
        column: position.column,
        selected,
        tabSize: options.tabSize,
        insertSpaces: options.insertSpaces,
        eol: model.getEOL() === "\r\n" ? "CRLF" : "LF",
      });
    };
    read();
    const subscriptions = [
      editor.onDidChangeCursorSelection(read),
      editor.onDidChangeModel(read),
      editor.onDidChangeModelOptions(read),
    ];
    return () => {
      for (const subscription of subscriptions) subscription.dispose();
    };
  }, [handle]);
  return status;
}
